/**
 * Composer strings: sender identity picker + undo-send toast (mounted under `inbox.*`). The
 * `confirm*` strings ask before a message goes out as someone else, the `gone*` ones say that a
 * conversation's sender can no longer send (PREC-4312).
 */
export const enInboxIdentity = {
	primary: "Primary",
	secondary: "Secondary",
	linkedin: "LinkedIn",
	linkedinInvite: "LinkedIn — will send invite",
	linkedinInvitationPending: "LinkedIn — invitation pending",
	linkedinUnknown: "LinkedIn — state unknown",
	linkedinStateUnknown:
		"We couldn't check this sender's LinkedIn connection to the lead. Reload the page to try again.",
	confirmTitle: "Send as someone else?",
	confirmOne: "Send this message as {{sender}}?",
	confirmMany: "Send these messages as {{sender}}?",
	confirmSeveral: "Send these messages as these senders?",
	confirmSend: "Send",
	goneSender: "{{name}}'s account isn't connected anymore — sending as you",
	goneSenderUnnamed: "This conversation's account isn't connected anymore — sending as you",
	chooseSender: "Choose who sends this message.",
	reconnectMailbox:
		"This mailbox needs a reconnect. Reconnect it in Personal settings → Connected Accounts.",
};

/**
 * The composer for a lead whose invitation from this sender is still pending, with no chat
 * (PREC-4216, mockup A2): the message waits for the acceptance instead of failing.
 */
export const enInboxPendingInvitation = {
	tag: "Invitation pending",
	whenTheyAccept: "when they accept",
	daysAfterTheyAccept_one: "{{count}} day after they accept",
	daysAfterTheyAccept_other: "{{count}} days after they accept",
	titleOwn: "{{name}} hasn't accepted your invitation yet",
	titleColleague: "{{name}} hasn't accepted {{sender}}'s invitation yet",
	theLead: "This lead",
	body: "LinkedIn delivers a message only after they accept. Choose what happens:",
	sendWhenAccepted: "Send when accepted",
	sendWhenAcceptedHint: "Dropped on {{date}} if the invitation isn't accepted by then",
	sendWhenAcceptedHintNoDeadline: "Dropped after 30 days without acceptance",
	sendFromColleague: "Send from {{sender}}",
	sendFromColleagueHint: "{{sender}} can message {{name}} now",
	scheduleAction: "Schedule for acceptance",
	scheduled:
		"Message scheduled. It sends as soon as the invitation is accepted. Click to view in Outbox",
	scheduledAfterDays_one:
		"Message scheduled. It sends {{count}} day after the invitation is accepted. Click to view in Outbox",
	scheduledAfterDays_other:
		"Message scheduled. It sends {{count}} days after the invitation is accepted. Click to view in Outbox",
	// The Undo toast of a deferred send that waits for the acceptance (never "Reply sent").
	undoScheduled: "Scheduled · sends when they accept",
	undoScheduledAfterDays_one: "Scheduled · sends {{count}} day after they accept",
	undoScheduledAfterDays_other: "Scheduled · sends {{count}} days after they accept",
	refused:
		'The invitation to this lead is still pending, so the message can\'t go out now. Choose "Send when accepted" to send it once they accept.',
	duplicate: "This message is already waiting for the invitation to be accepted.",
	batchUnsupported:
		"These messages can't all wait for the same invitation: one of them is another invitation, or comes from a sender whose own invitation is still pending. Send them separately.",
};

export const enInboxEditor = {
	stripPlaceholder: "Click here to edit your reply…",
	collapse: "Collapse editor",
};

export const enInboxUndoSend = {
	replySent: "Reply sent",
	followUpsScheduled_one: "{{count}} follow-up scheduled",
	followUpsScheduled_other: "{{count}} follow-ups scheduled",
	undo: "Undo",
	undone: "Send undone",
	undoFailed: "Too late to undo — the message was already sent",
};

/** The email draft's Include signature switch (PREC-4401). */
export const enInboxSignature = {
	label: "Include signature",
	noSignature:
		"This mailbox has no signature. Add one under Personal settings → Connected Accounts, in the mailbox's settings.",
};

export const enInboxVariable = {
	insert: "Insert variable",
	search: "Search variables…",
	noResults: "No variables found",
	unsubscribeChip: "Unsubscribe link",
	previewLabel: "Preview",
};
